import { useEffect, useState } from "react";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, CLINIC } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "General Enquiry", message: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => { document.title = "Contact — Manasvi Dental Clinic, Kota"; }, []);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Please fill required fields"); return; }
    setLoading(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Enquiry sent! We&apos;ll get back within 24 hours.");
      setForm({ name: "", email: "", phone: "", subject: "General Enquiry", message: "" });
    } catch {
      toast.error("Failed to send enquiry. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="anim-fade-up">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-8">
        <span className="text-xs uppercase tracking-[0.25em] text-primary">Contact</span>
        <h1 className="font-serif text-5xl sm:text-6xl tracking-tight mt-3 max-w-3xl">Let&apos;s talk about your smile.</h1>
        <p className="mt-6 text-lg text-muted-foreground max-w-2xl">Send us a message, give us a ring, or drop by the clinic — we&apos;d love to meet you.</p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-8 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5 space-y-6">
          <div className="bg-card border border-border rounded-2xl p-8">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-primary/15 grid place-items-center shrink-0"><MapPin className="w-5 h-5 text-primary" /></div>
              <div>
                <div className="font-serif text-xl">Visit Clinic</div>
                <div className="text-sm text-muted-foreground mt-1">{CLINIC.address}</div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4">
            <div className="bg-card border border-border rounded-2xl p-6 flex items-center gap-4">
              <Phone className="w-5 h-5 text-primary" />
              <div><div className="text-xs uppercase tracking-widest text-muted-foreground">Call</div><a href={`tel:${CLINIC.phone}`} className="font-medium">{CLINIC.phone}</a></div>
            </div>
            <div className="bg-card border border-border rounded-2xl p-6 flex items-center gap-4">
              <Mail className="w-5 h-5 text-primary" />
              <div><div className="text-xs uppercase tracking-widest text-muted-foreground">Email</div><a href={`mailto:${CLINIC.email}`} className="font-medium">{CLINIC.email}</a></div>
            </div>
            <div className="bg-card border border-border rounded-2xl p-6 flex items-center gap-4">
              <Clock className="w-5 h-5 text-primary" />
              <div><div className="text-xs uppercase tracking-widest text-muted-foreground">Hours</div><div className="text-sm">{CLINIC.hours}</div></div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={onSubmit} className="bg-card border border-border rounded-2xl p-8 space-y-5" data-testid="contact-form">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="c-name">Full Name*</Label>
                <Input id="c-name" name="name" value={form.name} onChange={onChange} required data-testid="contact-name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="c-email">Email*</Label>
                <Input id="c-email" type="email" name="email" value={form.email} onChange={onChange} required data-testid="contact-email" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="c-phone">Phone</Label>
                <Input id="c-phone" name="phone" value={form.phone} onChange={onChange} data-testid="contact-phone" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="c-subject">Subject</Label>
                <Input id="c-subject" name="subject" value={form.subject} onChange={onChange} data-testid="contact-subject" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="c-msg">Message*</Label>
              <Textarea id="c-msg" name="message" value={form.message} onChange={onChange} required rows={5} data-testid="contact-message" />
            </div>
            <Button type="submit" size="lg" className="rounded-full bg-primary hover:bg-primary/90 px-8" disabled={loading} data-testid="contact-submit">
              {loading ? "Sending..." : "Send Enquiry"}
            </Button>
          </form>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20">
        <div className="rounded-2xl overflow-hidden border border-border aspect-[16/7]">
          <iframe src={CLINIC.mapSrc} title="Clinic map" className="w-full h-full" loading="lazy" data-testid="contact-map" />
        </div>
      </section>
    </div>
  );
}
